import { useState, useEffect, useRef } from "react";
import { searchPatients } from "@/lib/api";
import { UI_CONSTANTS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

export interface PatientSearchResult {
  id: string;
  name: string;
  phone?: string;
  age?: number;
  date_of_birth?: string;
  gender?: string;
  address?: string;
  email?: string;
  last_visit?: string;
  registration_date?: string;
  mrn?: string;
}

interface UsePatientSearchResult {
  patients: PatientSearchResult[];
  isLoading: boolean;
}

export function usePatientSearch(query: string): UsePatientSearchResult {
  const { currentUser } = useAuth();
  const [patients, setPatients] = useState<PatientSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!query || query.trim().length < UI_CONSTANTS.MIN_PATIENT_SEARCH_LENGTH) {
      setPatients([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    debounceRef.current = setTimeout(async () => {
      try {
        const results = await searchPatients(query.trim(), currentUser?.clinic_id);

        // Handle different API response formats
        let patientsArray: PatientSearchResult[] = [];
        if (Array.isArray(results)) {
          patientsArray = results;
        } else if (results && typeof results === 'object' && Array.isArray((results as any).patients)) {
          patientsArray = (results as any).patients;
        } else if (results && typeof results === 'object' && Array.isArray((results as any).data)) {
          patientsArray = (results as any).data;
        }

        setPatients(patientsArray);
      } catch {
        setPatients([]);
      } finally {
        setIsLoading(false);
      }
    }, UI_CONSTANTS.DEBOUNCE_DELAY);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, currentUser?.clinic_id]);

  return { patients, isLoading };
}
